import { useState } from "react";
import type { AnalysisResultDto, RoiDto } from "@gazelab/shared";
import { fmtNum, fmtPct } from "../../lib/format.js";

export function RoiTable(props: {
  rois: RoiDto[];
  analysis: AnalysisResultDto | null;
  onRename: (roi: RoiDto, label: string) => void;
  onDelete: (roi: RoiDto) => void;
  onHover: (id: string | null) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const a = props.analysis;
  const byId = (list: AnalysisResultDto["groupA"]["rois"] | undefined, id: string) => list?.find((r) => r.roiId === id);
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm" data-testid="roi-table">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="py-1 pr-3">ROI</th>
            <th className="py-1 pr-3">Área (x, y, ancho, alto)</th>
            <th className="py-1 pr-3">{a ? `A: ${a.groupA.groupName}` : "Grupo A"}</th>
            <th className="py-1 pr-3">{a ? `B: ${a.groupB.groupName}` : "Grupo B"}</th>
            <th className="py-1 pr-3">Diferencia A − B</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {props.rois.map((r) => {
            const ra = byId(a?.groupA.rois, r.id);
            const rb = byId(a?.groupB.rois, r.id);
            const diff = a?.roiDifferences.find((d) => d.roiId === r.id)?.diffPp ?? null;
            return (
              <tr key={r.id} className="border-t border-slate-100" onMouseEnter={() => props.onHover(r.id)} onMouseLeave={() => props.onHover(null)}>
                <td className="py-1 pr-3">
                  {editing === r.id ? (
                    <form
                      className="flex gap-1"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (label.trim()) props.onRename(r, label.trim());
                        setEditing(null);
                      }}
                    >
                      <input aria-label="Nueva etiqueta" className="input py-1" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} autoFocus />
                      <button type="submit" className="btn-secondary px-2 py-1 text-xs">
                        OK
                      </button>
                    </form>
                  ) : (
                    <span className="font-medium">{r.label}</span>
                  )}
                </td>
                <td className="py-1 pr-3 font-mono text-xs">
                  {fmtNum(r.geometry.x, 3)}, {fmtNum(r.geometry.y, 3)}, {fmtNum(r.geometry.width, 3)}, {fmtNum(r.geometry.height, 3)}
                </td>
                <td className="py-1 pr-3">
                  {fmtPct(ra?.meanFraction)} {ra?.sd != null && <span className="text-xs text-slate-500">(DE {fmtPct(ra.sd)})</span>}
                </td>
                <td className="py-1 pr-3">
                  {fmtPct(rb?.meanFraction)} {rb?.sd != null && <span className="text-xs text-slate-500">(DE {fmtPct(rb.sd)})</span>}
                </td>
                <td className="py-1 pr-3 font-semibold">{diff === null ? "—" : `${diff > 0 ? "+" : ""}${fmtNum(diff, 1)} pp`}</td>
                <td className="whitespace-nowrap py-1">
                  <button
                    type="button"
                    className="btn-secondary px-2 py-1 text-xs"
                    onClick={() => {
                      setEditing(r.id);
                      setLabel(r.label);
                    }}
                  >
                    Renombrar
                  </button>{" "}
                  <button type="button" className="btn-danger px-2 py-1 text-xs" onClick={() => props.onDelete(r)}>
                    Eliminar
                  </button>
                </td>
              </tr>
            );
          })}
          {props.rois.length === 0 && (
            <tr>
              <td colSpan={6} className="py-2 text-slate-500">
                Sin ROIs. Activa «Dibujar ROI» y arrastra sobre la imagen.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="mt-1 text-xs text-slate-500">
        Porcentaje medio de muestras estimadas en imagen que caen dentro de cada ROI, calculado por participante y luego promediado (cada persona pesa igual). Bordes incluidos.
      </p>
    </div>
  );
}
